/**
 * A validator compiled from the payload schema the manifest already declares.
 *
 * Step 5 of the required action algorithm says the payload must be checked
 * against "the schema the release manifest declared". Asking the game author to
 * hand-write that check in TypeScript — which is what the earlier example did —
 * creates two definitions of the same thing and no mechanism to keep them equal.
 * The one in the manifest is enforced server-side; the one in the game is what
 * actually gates the handler. When they drift, the handler runs on a payload the
 * author believes is impossible.
 *
 * So the game passes the manifest's `payloadSchema` object itself, and this
 * module turns it into a predicate.
 *
 * The deliberate limitation: this understands a *subset* of JSON Schema, and
 * `compilePayloadSchema` throws on any keyword outside that subset rather than
 * ignoring it. Silently skipping an unrecognised constraint is the one failure
 * mode that would matter here — it would under-validate while looking like it
 * validated — so an unsupported schema is a loud error at registration time,
 * pointing the author at the escape hatch of supplying a function instead.
 *
 * There is no `eval` and no `Function` constructor anywhere in this file: a
 * hosted game's CSP has no `unsafe-eval`, so a conventional schema compiler
 * could not run at all.
 */
import { TRYPORTALError } from './errors.js';
const SUPPORTED_KEYWORDS = new Set([
    'type',
    'properties',
    'required',
    'additionalProperties',
    'enum',
    'const',
    'minimum',
    'maximum',
    'exclusiveMinimum',
    'exclusiveMaximum',
    'multipleOf',
    'minLength',
    'maxLength',
    'pattern',
    'items',
    'minItems',
    'maxItems',
    'uniqueItems',
    'description',
    'title',
    'default',
    'examples',
]);
const SUPPORTED_TYPES = new Set([
    'object',
    'array',
    'string',
    'number',
    'integer',
    'boolean',
    'null',
]);
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function reject(path, message) {
    throw new TRYPORTALError('INVALID_USAGE', `unsupported payload schema at ${path}: ${message}. ` +
        'Pass a validate(payload) function to onAction() instead of the schema object.');
}
function assertSupported(schema, path) {
    if (!isRecord(schema))
        reject(path, 'a schema must be an object');
    for (const keyword of Object.keys(schema)) {
        if (!SUPPORTED_KEYWORDS.has(keyword)) {
            reject(path, `the keyword "${keyword}" is not supported by this compiler`);
        }
    }
    const type = schema['type'];
    if (type !== undefined) {
        const types = Array.isArray(type) ? type : [type];
        for (const entry of types) {
            if (typeof entry !== 'string' || !SUPPORTED_TYPES.has(entry)) {
                reject(path, `unknown type ${JSON.stringify(entry)}`);
            }
        }
    }
    const properties = schema['properties'];
    if (properties !== undefined) {
        if (!isRecord(properties))
            reject(`${path}/properties`, 'must be an object');
        for (const [key, value] of Object.entries(properties)) {
            assertSupported(value, `${path}/properties/${key}`);
        }
    }
    const items = schema['items'];
    if (items !== undefined)
        assertSupported(items, `${path}/items`);
    const additional = schema['additionalProperties'];
    if (additional !== undefined && typeof additional !== 'boolean') {
        reject(`${path}/additionalProperties`, 'only true or false is supported');
    }
}
function matchesType(value, type) {
    switch (type) {
        case 'object':
            return isRecord(value);
        case 'array':
            return Array.isArray(value);
        case 'string':
            return typeof value === 'string';
        case 'integer':
            return typeof value === 'number' && Number.isSafeInteger(value);
        case 'number':
            return typeof value === 'number' && Number.isFinite(value);
        case 'boolean':
            return typeof value === 'boolean';
        case 'null':
            return value === null;
        default:
            return false;
    }
}
function validateAgainst(schema, value) {
    const type = schema['type'];
    if (type !== undefined) {
        const types = Array.isArray(type) ? type : [type];
        if (!types.some((entry) => matchesType(value, entry)))
            return false;
    }
    if ('const' in schema && !deepEqual(schema['const'], value))
        return false;
    const enumValues = schema['enum'];
    if (Array.isArray(enumValues) && !enumValues.some((entry) => deepEqual(entry, value))) {
        return false;
    }
    if (typeof value === 'number') {
        const min = schema['minimum'];
        if (typeof min === 'number' && value < min)
            return false;
        const max = schema['maximum'];
        if (typeof max === 'number' && value > max)
            return false;
        const exclusiveMin = schema['exclusiveMinimum'];
        if (typeof exclusiveMin === 'number' && value <= exclusiveMin)
            return false;
        const exclusiveMax = schema['exclusiveMaximum'];
        if (typeof exclusiveMax === 'number' && value >= exclusiveMax)
            return false;
        const multipleOf = schema['multipleOf'];
        if (typeof multipleOf === 'number' && multipleOf > 0) {
            const quotient = value / multipleOf;
            if (Math.abs(quotient - Math.round(quotient)) > 1e-9)
                return false;
        }
    }
    if (typeof value === 'string') {
        const minLength = schema['minLength'];
        if (typeof minLength === 'number' && value.length < minLength)
            return false;
        const maxLength = schema['maxLength'];
        if (typeof maxLength === 'number' && value.length > maxLength)
            return false;
        const pattern = schema['pattern'];
        if (typeof pattern === 'string' && !new RegExp(pattern).test(value))
            return false;
    }
    if (Array.isArray(value)) {
        const minItems = schema['minItems'];
        if (typeof minItems === 'number' && value.length < minItems)
            return false;
        const maxItems = schema['maxItems'];
        if (typeof maxItems === 'number' && value.length > maxItems)
            return false;
        if (schema['uniqueItems'] === true) {
            const seen = [];
            for (const entry of value) {
                if (seen.some((other) => deepEqual(other, entry)))
                    return false;
                seen.push(entry);
            }
        }
        const items = schema['items'];
        if (isRecord(items)) {
            for (const entry of value) {
                if (!validateAgainst(items, entry))
                    return false;
            }
        }
    }
    if (isRecord(value)) {
        const properties = isRecord(schema['properties']) ? schema['properties'] : {};
        const required = schema['required'];
        if (Array.isArray(required)) {
            for (const key of required) {
                if (typeof key !== 'string' || !(key in value))
                    return false;
            }
        }
        if (schema['additionalProperties'] === false) {
            for (const key of Object.keys(value)) {
                if (!(key in properties))
                    return false;
            }
        }
        for (const [key, subSchema] of Object.entries(properties)) {
            if (!(key in value))
                continue;
            if (!isRecord(subSchema))
                return false;
            if (!validateAgainst(subSchema, value[key]))
                return false;
        }
    }
    return true;
}
function deepEqual(a, b) {
    if (a === b)
        return true;
    if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((entry, index) => deepEqual(entry, b[index]));
    }
    if (isRecord(a) && isRecord(b)) {
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        return (aKeys.length === bKeys.length && aKeys.every((key) => key in b && deepEqual(a[key], b[key])));
    }
    return false;
}
/**
 * Compile a manifest `payloadSchema` into a predicate.
 *
 * Throws `INVALID_USAGE` at registration time — not at action time — if the
 * schema uses anything this subset does not cover.
 */
export function compilePayloadSchema(schema) {
    assertSupported(schema, '#');
    const root = schema;
    return (payload) => {
        try {
            return validateAgainst(root, payload);
        }
        catch {
            return false;
        }
    };
}
